import React from 'react'
import {CardData, FormSetting} from '../../../type'
import {PromoAction} from '../../../../../../../services/cellcast.v2.types'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import './PreviewFormSetting.scss'
import {DEFAULT_FORM_FIELDS} from '../../../../../../Utils/Constant/WalletCard'
import {constantsRegion} from '../../../../../../Utils/_helpers'
import PhoneInput from '../../../../../../Components/PhoneInput'
import '../../../../../../Components/Style/PhoneInput.scss'

interface PreviewFormSettingProps {
  cardData: CardData
  promoActionLabels?: string[]
  showFormSettingModal: boolean
  settingFormFields?: FormSetting[]
}

const PreviewFormSetting = ({
  cardData,
  showFormSettingModal = false,
  settingFormFields = [],
  promoActionLabels = [],
}: PreviewFormSettingProps) => {
  const {t} = useI18n()

  // Use cardData.formSettings as primary source, settingFormFields as fallback
  const fieldsToShow =
    cardData.formSettings && cardData.formSettings.length > 0
      ? [...cardData.formSettings]
          .filter(field => field.isDisplay !== false) // Only show enabled fields
          .sort((a, b) => a.order - b.order) // Sort by order
      : settingFormFields.length > 0
      ? settingFormFields
          .filter(field => field.isDisplay !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0))
      : DEFAULT_FORM_FIELDS

  // Mock promo settings for preview (since we only have IDs)
  const mockPromoSettings: PromoAction[] =
    promoActionLabels.length > 0
      ? promoActionLabels.map((label, index) => ({
          id: index.toString(),
          name: label,
        }))
      : cardData.promoSettings


  const mockVehicleTypeOptions = (cardData.vehicleTypeOptions || []).map((id, index) => ({
    id,
    name: `Vehicle Type ${index + 1}`,
  }))

  // Map field placeholders to input types
  const getInputType = (key: string) => {
    switch (key.toLowerCase()) {
      case 'email':
        return 'email'
      case 'phone':
        return 'tel'
      case 'website':
        return 'url'
      default:
        return 'text'
    }
  }

  // Get translation key for field placeholder
  const getFieldLabel = (placeholder: string) => {
    const labelMap: {[key: string]: string} = {
      'Full Name': t('WALLET_CARD_PUBLIC.FULL_NAME'),
      Phone: t('WALLET_CARD_PUBLIC.PHONE'),
      Email: t('WALLET_CARD_PUBLIC.EMAIL'),
      'Company Name': t('WALLET_CARD_PUBLIC.COMPANY_NAME'),
      'Job Title': 'Job Title',
      Address: 'Address',
      Website: 'Website',
      LinkedIn: 'LinkedIn',
      Notes: 'Notes',
    }
    return labelMap[placeholder] || placeholder
  }

  return (
    <div
      className='preview-form-setting-container'
      style={{backgroundColor: cardData.formBackgroundColor, color: cardData.formLabelColor}}
    >
      {/* Header Section */}
      <div className='preview-form-header'>
        <div className={`icon-container ${cardData.picture ? '' : 'd-none'}`}>
          <img
            alt='Avatar'
            src={cardData.picture}
            crossOrigin='anonymous'
            className='rounded-circle object-fit-cover w-100 h-100 logo-circle icon-circle'
          />
        </div>
        <div className='title-preview-form'>
          <h3 style={{color: cardData.formLabelColor}}>
            {t('WALLET_CARD_PUBLIC.SHARE_CONTACT_INFORMATION', {
              name: cardData.fullName || 'John Doe',
            })}
          </h3>
        </div>
      </div>

      {/* Form Content */}
      <div className='preview-form-content'>
        {/* Promo Actions Preview */}
        {mockPromoSettings.length > 0 && (
          <div className='form-group mb-4'>
            <label className='form-label' style={{color: cardData.formLabelColor}}>
              {t('WALLET_CARD_PUBLIC.PROMO_ACTION')} <span className='required-field'>*</span>
            </label>
            <div className='promo-actions-container'>
              {mockPromoSettings.map((promo: PromoAction) => (
                <div key={promo.id} className='promo-action-item preview-promo'>
                  <div className='promo-action-checkbox preview-checkbox'></div>
                  <span className='promo-text'>{promo.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Vehicle Types Preview (restricted by template) */}
        {cardData.vehicleTypeOptions &&
          cardData.vehicleTypeOptions.length > 0 &&
          mockVehicleTypeOptions.length > 0 && (
            <div className='form-group mb-4'>
              <label className='form-label' style={{color: cardData.formLabelColor}}>
                {t('WALLET_CARD_PUBLIC.INTERESTED_VEHICLE_TYPE')}
              </label>
              <select className='form-select' disabled value=''>
                <option value='' disabled>
                  Select vehicle
                </option>
                {mockVehicleTypeOptions.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>
          )}

        {/* Dynamic Form Fields Preview */}
        {fieldsToShow.map((field: any) => {
          // Handle both FormSetting (with _id) and FormField (with id) types
          const isFormSetting = '_id' in field
          const fieldKey = isFormSetting ? field._id : `field_${field.key || ''}`
          const fieldLabel = field.label
          const fieldRequired = isFormSetting ? field.isRequired : (field as any).required
          const fieldKeyForType = field.key

          // Check if this is a phone field
          const isPhoneField = fieldKeyForType?.toLowerCase() === 'phone'
          const isVehicleTypeField = fieldKeyForType === 'interestedVehicleType'

          return (
            isVehicleTypeField ? null : (
            <div key={fieldKey} className='form-group mb-4'>
              <label className='form-label' style={{color: cardData.formLabelColor}}>
                {getFieldLabel(fieldLabel)}
                {fieldRequired && <span className='required-field'> *</span>}
              </label>

              {isPhoneField ? (
                <PhoneInput
                  value=""
                  countryCode="AU"
                  onChange={() => {}} // No-op for preview
                  onBlur={() => {}} // No-op for preview
                  placeholder={t('WALLET_CARD.PLACEHOLDER.PHONE')}
                  required={fieldRequired}
                  disabled={true}
                  name={fieldKeyForType}
                  className="preview-phone-input"
                  label="" // Don't show PhoneInput's internal label
                />
              ) : (
                <input
                  type={getInputType(fieldKeyForType)}
                  className='form-control preview-input'
                  placeholder={`Enter your ${fieldLabel.toLowerCase()}`}
                  disabled
                  readOnly
                />
              )}
            </div>
            )
          )
        })}

        {/* Submit Button */}
        <div className='submit-section'>
          <button type='button' className='submit-btn preview-button' disabled>
            {t('WALLET_CARD_PUBLIC.CONNECT')}
          </button>
          <div className='powered-by' style={{color: cardData.formLabelColor}}>
            {t('WALLET_CARD_PUBLIC.POWERED_BY')}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PreviewFormSetting
